/**
 * What we do to an exception before it is sent.
 *
 * Drop noise that is not a fault (cancellations, link-scanner rejections,
 * a stale tab asking for a chunk from the previous deploy, and a browser
 * translator moving a node out from under React), identify
 * browser-translated pages, and give every app-owned hydration mismatch
 * enough context to name its cause.
 *
 * The pure functions here take plain data rather than reading globals, so the
 * decisions can be tested. Only `readHydrationEnvironment` touches the DOM.
 */
import { isChunkLoadError } from './chunk-load-error';

/** One entry of PostHog's `$exception_list`. */
export interface ExceptionValue {
  type?: unknown;
  value?: unknown;
}

/** Reads the exception list off an event without trusting its shape. */
export function readExceptionValues(properties: unknown): ExceptionValue[] {
  if (!properties || typeof properties !== 'object') {
    return [];
  }

  const list = (properties as { $exception_list?: unknown }).$exception_list;

  return Array.isArray(list) ? (list as ExceptionValue[]) : [];
}

function typeOf(value: ExceptionValue) {
  return typeof value?.type === 'string' ? value.type : '';
}

function messageOf(value: ExceptionValue) {
  return typeof value?.value === 'string' ? value.value : '';
}

/**
 * Whether every value in the list is a cancelled request.
 *
 * `AbortError` arrives whenever an in-flight fetch is deliberately cancelled:
 * a route prefetch dropped because the visitor navigated first, a stream we
 * abort ourselves when the user sends a second message, a component unmounting
 * mid-request. Every one of those is the system working, and an exception list
 * nobody trusts is one nobody reads.
 *
 * This drops the abort exception itself, not the failure of whatever was being
 * fetched — an list with one abort and one real error is kept.
 */
export function isAbortOnly(values: ExceptionValue[]): boolean {
  if (values.length === 0) {
    return false;
  }

  return values.every((value) => {
    const type = typeOf(value);
    const message = messageOf(value);

    return (
      type === 'AbortError' ||
      /* Safari and Firefox word it differently; all three mean cancelled. */
      /\b(aborted|abort)\b/i.test(message)
    );
  });
}

/**
 * Rejections emitted by Chromium wrappers that scan links from email and
 * search results.
 *
 * The `Object Not Found Matching Id` wording is produced by CefSharp's
 * JavaScript bridge, not by an application object or one of our data clients.
 * These visits often advertise an ordinary Chrome user agent, so the stable
 * rejection signature is the only dependable discriminator. Keep the match
 * narrow so an actual application rejection is never hidden with it.
 */
const LINK_SCANNER_REJECTION =
  /^Non-Error promise rejection captured with value:\s*Object Not Found Matching Id:\d+,\s*MethodName:update,\s*ParamCount:\d+\s*$/i;

export function isLinkScannerRejection(values: ExceptionValue[]): boolean {
  return (
    values.length > 0 &&
    values.every(
      (value) =>
        typeOf(value) === 'UnhandledRejection' &&
        LINK_SCANNER_REJECTION.test(messageOf(value)),
    )
  );
}

/**
 * Whether every value is a stale-deploy chunk failure.
 *
 * A tab left open across a deploy asks for a hashed file that is gone. The
 * page reloads once into the new build. A list that is only that failure is
 * noise. A real error beside it is kept.
 */
export function isChunkLoadException(values: ExceptionValue[]): boolean {
  if (values.length === 0) {
    return false;
  }

  return values.every((value) =>
    isChunkLoadError({
      name: typeOf(value),
      message: messageOf(value),
    }),
  );
}

/**
 * Whether a Sentry event is only a stale-deploy chunk failure.
 *
 * `ignoreErrors` sees the message, and the `Type: message` form when both
 * exist. A `ChunkLoadError` with an empty message never reaches that check,
 * so the client `beforeSend` uses this as well.
 */
export function isChunkLoadSentryEvent(event: {
  message?: unknown;
  exception?: { values?: ExceptionValue[] | null } | null;
}): boolean {
  const values = event.exception?.values;

  if (Array.isArray(values) && values.length > 0) {
    return isChunkLoadException(values);
  }

  return isChunkLoadException([{ type: 'Error', value: event.message }]);
}

/**
 * Chrome and Firefox wording when removeChild or insertBefore is aimed at a
 * node that parent no longer owns.
 *
 * Google Translate wraps text in font elements, so React's own parent is
 * wrong and the DOM throws NotFoundError. The client guard skips that call.
 * This pattern is how monitoring drops the same error if a throw still
 * escapes. The match needs both the method name and the parent wording, so
 * an ordinary NotFoundError is kept.
 */
export const TRANSLATED_DOM_MUTATION_MESSAGE =
  /(?:removeChild|insertBefore)[\s\S]{0,180}not a child of this node|not a child of this node[\s\S]{0,180}(?:removeChild|insertBefore)/i;

/** Whether this text is the translator parent mismatch, not some other error. */
export function isTranslatedDomMutationMessage(message: string): boolean {
  return TRANSLATED_DOM_MUTATION_MESSAGE.test(message);
}

function translatedDomText(value: ExceptionValue) {
  return `${typeOf(value)}: ${messageOf(value)}`;
}

/**
 * Whether every value is a translator parent mismatch.
 *
 * A list that is only that DOMException is noise: the guard already skipped
 * the call, and a throw that still escapes is the translator, not our render.
 * A real error beside it is kept.
 */
export function isTranslatedDomMutationException(
  values: ExceptionValue[],
): boolean {
  if (values.length === 0) {
    return false;
  }

  return values.every((value) =>
    isTranslatedDomMutationMessage(translatedDomText(value)),
  );
}

/** Whether a thrown value is the translator parent mismatch. */
export function isTranslatedDomMutationError(error: unknown): boolean {
  if (typeof error === 'string') {
    return isTranslatedDomMutationMessage(error);
  }

  if (!error || typeof error !== 'object') {
    return false;
  }

  const record = error as { name?: unknown; message?: unknown };
  const name = typeof record.name === 'string' ? record.name : '';
  const message = typeof record.message === 'string' ? record.message : '';

  return isTranslatedDomMutationMessage(`${name}: ${message}`);
}

/**
 * Whether a Sentry event is only a translator parent mismatch.
 *
 * `ignoreErrors` sees the message. `beforeSend` uses this as well so an
 * event whose text lives on the exception value is dropped the same way.
 */
export function isTranslatedDomSentryEvent(event: {
  message?: unknown;
  exception?: { values?: ExceptionValue[] | null } | null;
}): boolean {
  const values = event.exception?.values;

  if (Array.isArray(values) && values.length > 0) {
    return isTranslatedDomMutationException(values);
  }

  return isTranslatedDomMutationException([
    { type: 'Error', value: event.message },
  ]);
}

/**
 * React's hydration failures, minified and not.
 *
 * 418 and 423 are the whole-root mismatches, 422 is the same inside a Suspense
 * boundary, 425 is a text node that differs. Production only ever gives us the
 * number, so the number is what we match on first; the prose alternatives are
 * there for a build with unminified React, and for the wording Next.js adds.
 */
const HYDRATION_MESSAGE =
  /Minified React error #(?:418|422|423|425)\b|Hydration failed|hydrating|Text content does not match/i;

/** Whether this exception is a hydration mismatch rather than a real fault. */
export function isHydrationException(values: ExceptionValue[]): boolean {
  return values.some((value) => HYDRATION_MESSAGE.test(messageOf(value)));
}

/**
 * The facts about a page that decide whether it hydrates.
 *
 * Everything here is either something that rewrites the DOM before React
 * reaches it, or something that makes our own render produce different text on
 * the server than in the browser. A mismatch that survives our own code is one
 * of these, and the report has never said which.
 */
export interface HydrationEnvironment {
  /** Path only. The query string adds noise and can carry personal data. */
  pathname: string;
  /** `<html>` classes: next-themes writes here, and so does Google Translate. */
  htmlClassName: string;
  /** Attribute names on `<html>` and `<body>`. We render almost none of these. */
  rootAttributeNames: string[];
  /** Inline `<script>` elements in `<head>`. We render none. */
  inlineHeadScriptCount: number;
  /** Translation wraps every text node it rewrites in a `<font>` element. */
  hasTranslationFontTags: boolean;
  /** Language declared by our document, normally `en`. */
  documentLanguage: string;
  language: string;
  /** A locale query added by translated Google search results. */
  translationQueryLanguage: string;
  /** Whether `sl`, `tl`, and Google's search-result client marker are present. */
  hasTranslatedResultQuery: boolean;
  timeZone: string;
  prefersReducedMotion: boolean;
  prefersDark: boolean;
}

/**
 * Attributes we or the framework put on `<html>` and `<body>` ourselves.
 *
 * Anything else on those two elements was added by something in the visitor's
 * browser, which is the single most useful thing to know about a mismatch we
 * cannot reproduce.
 */
const OWN_ROOT_ATTRIBUTES = new Set([
  'class',
  'lang',
  'dir',
  'style',
  'data-theme',
  'data-scroll-locked',
]);

export type HydrationSuspect =
  | 'browser-translation'
  | 'injected-head-script'
  | 'browser-extension'
  | 'unknown';

/**
 * The one column worth grouping by.
 *
 * Ranked by how completely each explains a mismatch: translation rewrites
 * every text node on the page, an injected head script runs while the HTML is
 * still parsing (this is what the pixels used to do), and a foreign attribute
 * on `<html>` or `<body>` means an extension reached the document at all.
 */
export function suspectFor(env: HydrationEnvironment): HydrationSuspect {
  if (
    env.hasTranslationFontTags ||
    /\btranslated-(?:ltr|rtl)\b/.test(env.htmlClassName) ||
    queryRequestsAnotherLanguage(env)
  ) {
    return 'browser-translation';
  }

  if (env.inlineHeadScriptCount > 0) {
    return 'injected-head-script';
  }

  const foreign = env.rootAttributeNames.filter(
    (name) => !OWN_ROOT_ATTRIBUTES.has(name),
  );

  return foreign.length > 0 ? 'browser-extension' : 'unknown';
}

function primaryLanguage(language: string) {
  return language.trim().toLowerCase().split(/[-_]/, 1)[0] ?? '';
}

/**
 * Google search can open an automatically translated result on the original
 * host and leave only `?hl=<locale>` behind. Current Android Chrome no longer
 * reliably leaves the old `<font>` wrappers or `translated-ltr` class, but it
 * can still replace text before React hydrates it. A requested language that
 * differs from the document language is the remaining durable signal.
 */
function queryRequestsAnotherLanguage(env: HydrationEnvironment) {
  const requested = primaryLanguage(env.translationQueryLanguage);
  const document = primaryLanguage(env.documentLanguage);
  const browser = primaryLanguage(env.language);

  return Boolean(
    requested &&
    env.hasTranslatedResultQuery &&
    document &&
    browser &&
    requested !== document &&
    requested === browser,
  );
}

/** Flattens the environment into properties PostHog can group and filter on. */
export function describeHydrationEnvironment(env: HydrationEnvironment) {
  return {
    hydration_suspect: suspectFor(env),
    hydration_pathname: env.pathname,
    hydration_html_class: env.htmlClassName,
    hydration_root_attributes: env.rootAttributeNames
      .filter((name) => !OWN_ROOT_ATTRIBUTES.has(name))
      .join(' '),
    hydration_inline_head_scripts: env.inlineHeadScriptCount,
    hydration_document_language: env.documentLanguage,
    hydration_language: env.language,
    hydration_translation_query_language: env.translationQueryLanguage,
    hydration_translated_result_query: env.hasTranslatedResultQuery,
    hydration_time_zone: env.timeZone,
    hydration_reduced_motion: env.prefersReducedMotion,
    hydration_dark: env.prefersDark,
  };
}

function attributeNames(element: Element | null): string[] {
  if (!element) {
    return [];
  }

  return Array.from(element.attributes, (attribute) => attribute.name);
}

function matches(query: string): boolean {
  try {
    return window.matchMedia(query).matches;
  } catch {
    return false;
  }
}

/**
 * Reads the environment out of the live document.
 *
 * Called only while sending an exception that is already known to be a
 * hydration failure, so the cost is paid on a page that is broken anyway.
 */
export function readHydrationEnvironment(): HydrationEnvironment | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }

  let timeZone = '';

  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    /* Some locked-down browsers refuse to resolve one. */
  }

  const headScripts = Array.from(
    document.head?.querySelectorAll('script') ?? [],
  );

  let translationQueryLanguage = '';
  let hasTranslatedResultQuery = false;

  try {
    const params = new URLSearchParams(window.location?.search ?? '');
    translationQueryLanguage = params.get('tl') ?? params.get('hl') ?? '';
    hasTranslatedResultQuery =
      params.has('sl') &&
      params.has('tl') &&
      params.get('client')?.toLowerCase() === 'srp';
  } catch {
    /* A synthetic location object may reject access to its search params. */
  }

  return {
    pathname: window.location?.pathname ?? '',
    htmlClassName: document.documentElement?.className ?? '',
    rootAttributeNames: [
      ...attributeNames(document.documentElement),
      ...attributeNames(document.body),
    ],
    inlineHeadScriptCount: headScripts.filter((script) => !script.src).length,
    hasTranslationFontTags: Boolean(document.body?.querySelector('font')),
    documentLanguage: document.documentElement?.lang ?? '',
    language: navigator?.language ?? '',
    translationQueryLanguage,
    hasTranslatedResultQuery,
    timeZone,
    prefersReducedMotion: matches('(prefers-reduced-motion: reduce)'),
    prefersDark: matches('(prefers-color-scheme: dark)'),
  };
}
